// src/components/admin/PlanManagement.jsx
import React, { useEffect, useState } from 'react';
import PlanForm from './PlanForm';
import { toast } from 'react-hot-toast';
import { fetchWithAuth } from './../../utils/fetchWithAuth';


const API = process.env.REACT_APP_API_BASE;
const PlanManagement = () => {
  const [plans, setPlans] = useState([]);
  const [apps, setApps] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [industries, setIndustries] = useState([]);

  useEffect(() => {
    fetchPlans();
    fetchApps();
    fetchIndustries();
  }, []);

  const getIndustryName = (industry) => {
    if (typeof industry === 'object' && industry?.displayName) return industry.displayName;
    return industries.find(i => i._id === industry)?.displayName || '—';
  };
  

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API}/admin/plans`);
      const data = await res.json();

      // Ensure country name is included properly for UI
      const enriched = data.map(plan => ({
        ...plan,
        country: plan.country && typeof plan.country === 'object' && 'name' in plan.country
          ? plan.country
          : { name: '—' }
      }));
console.log(enriched);
      setPlans(enriched);
    } catch (err) {
      toast.error('Failed to fetch plans');
    }
  };

  const fetchApps = async () => {
    try {
      const res = await fetch(`${API}/admin/apps`);
      const data = await res.json();
      setApps(data);
    } catch (err) {
      toast.error('Failed to fetch apps');
    }
  };

  const fetchIndustries = async () => {
    try {
      const res = await fetchWithAuth(`${API}/api/industries`);
      const data = await res.json();
  
      const industriesArray = Array.isArray(data)
        ? data
        : Array.isArray(data.industries)
          ? data.industries
          : Array.isArray(data.data)
            ? data.data
            : [];
  
      setIndustries(industriesArray);
    } catch (err) {
      toast.error('Failed to fetch industries');
      setIndustries([]); // fallback to avoid crash
    }
  };
  

  const handleSubmit = async (form) => {
    const method = editingPlan ? 'PUT' : 'POST';
    const url = editingPlan
      ? `${API}/admin/plans/${editingPlan._id}`
      : `${API}/admin/plans`;

    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        toast.success(`Plan ${editingPlan ? 'updated' : 'created'} successfully`);
        fetchPlans();
        setEditingPlan(null);
      } else {
        toast.error('Failed to save plan');
      }
    } catch (err) {
      toast.error('Server error');
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;

    try {
      const res = await fetch(`${API}/admin/plans/${id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Plan deleted');
        fetchPlans();
      } else {
        toast.error('Failed to delete plan');
      }
    } catch (err) {
      toast.error('Server error');
    }
  };

  return (
    <div className="space-y-6">
      <PlanForm
        apps={apps}
        industries={industries}
        editingPlan={editingPlan}
        onSubmit={handleSubmit}
        onCancel={() => setEditingPlan(null)}
      />

      <div className="bg-white border rounded-md p-4 shadow">
        <h3 className="text-lg font-semibold mb-4">Plans List</h3>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 text-left">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Price (M/Y)</th>
              <th className="px-4 py-2">Seats</th>
              <th className="px-4 py-2">Requests</th>
              <th className="px-4 py-2">Tokens</th>
              <th className="px-4 py-2">Country</th>
              <th className="px-4 py-2">Industry</th>
              <th className="px-4 py-2">Apps</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {plans.map(plan => (
              <tr key={plan._id} className="border-t">
                <td className="px-4 py-2">{plan.name}</td>
                <td className="px-4 py-2">{plan.monthlyPrice} / {plan.yearlyPrice}</td>
                <td className="px-4 py-2">{plan.seats}</td>
                <td className="px-4 py-2">{plan.requestLimit}</td>
                <td className="px-4 py-2">{plan.tokenLimit}</td>
                <td className="px-4 py-2">{plan.country?.name || '—'}</td>
                <td>{getIndustryName(plan.industryId)}</td>
                <td className="px-4 py-2">
                  <span
                    className="text-sm text-blue-600 cursor-pointer underline"
                    onClick={() => alert(plan.apps.join(', '))}
                  >
                    {plan.apps.length} app(s)
                  </span>
                </td>
                <td className="px-4 py-2 space-x-2">
                  <button className="text-blue-600 text-xs underline" onClick={() => handleEdit(plan)}>
                    Edit
                  </button>
                  <button className="text-red-600 text-xs underline" onClick={() => handleDelete(plan._id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {plans.length === 0 && (
              <tr>
                <td className="px-4 py-4 text-center text-gray-500" colSpan="8">
                  No plans found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PlanManagement;
